import Vue from 'vue';
import Draw from 'ol/interaction/Draw';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import TileWMS from 'ol/source/TileWMS';
import { fromCircle } from 'ol/geom/Polygon';
import { transform } from 'ol/proj';
import { UrlHandler } from "@/services/utils/UrlHandler.js"
import { mapService } from "@/services/map/mapService";
import GeoJSON from 'ol/format/GeoJSON';
import EsriJSON from 'ol/format/EsriJSON';

// --- HITBOX TOLERANCE CONSTANTS ---
// Tune these to match the largest point symbol size in your GeoServer SLDs / ArcGIS styles.
const WMS_HIT_BUFFER_PX = 16;       // pixels expanded around click for WMS GetFeatureInfo (GeoServer BUFFER param)
const ARCGIS_HIT_TOLERANCE_PX = 16; // screen-pixel radius used to build the ArcGIS spatial envelope

class SelectService {
    constructor() {
        this.map = null;
        this.drawInteraction = null;
        this.selectionLayer = null;
        this.activeLayers = [];
        this.clickListener = null;

        // Reactive State for UI
        this.state = Vue.observable({
            features: [],
            location: null
        });
    }

    init(map) {
        this.map = map;
        this.createSelectionLayer();
    }

    setMap(map) {
        this.map = map;
    }

    setActiveLayers(layers) {
        this.activeLayers = layers || [];
    }

    createSelectionLayer() {
        if (!this.selectionLayer) {
            this.selectionLayer = new VectorLayer({
                source: new VectorSource(),
                zIndex: 9999
            });
        }
    }

    isListening() {
        return this.clickListener !== null;
    }

    // --- STATE MANAGEMENT ---

    setSelection(coordinate, features) {
        this.state.location = coordinate;
        this.state.features = features || [];
    }

    clearResults() {
        this.state.location = null;
        this.state.features = [];

        if (this.selectionLayer) {
            this.selectionLayer.getSource().clear();
        }
    }

    // --- DATA FETCHING (Clean Code Segregated Methods) ---

    async getPointFeatures(coordinate, resolution, projection, layers = null) {
        console.log('Fetching point features');
        const targetLayers = layers || this.activeLayers;
        if (!targetLayers || targetLayers.length === 0) {
            return [];
        }
        console.log('Target layers:', targetLayers);
        const promises = [...targetLayers].reverse().map(async layer => {
            const provider = layer.provider ? layer.provider.name : 'wms';
            try {
                if (provider === 'geoserver') {
                    return await this.fetchWMSFeatureInfo(layer, coordinate, resolution, projection);
                } else if (provider === 'arcgis') {
                    return await this.fetchArcGISFeatureInfo(layer, coordinate, resolution);
                }
            } catch (error) {
                console.warn(`Error fetching info for layer ${layer.name}:`, error);
                return null;
            }
            return null;
        });

        const results = await Promise.all(promises);

        // Flatten and clean results (remove nulls)
        return results
            .filter(res => res && res.features && res.features.length > 0)
            .flatMap(res => res.features.map(f => ({
                ...f,
                layerName: res.layerName // Ensure layer name is attached
            })));
    }

    /**
     * Fetches GetFeatureInfo from a WMS source (GeoServer)
     */
    async fetchWMSFeatureInfo(layer, coordinate, resolution, projection) {
        console.log('Fetching WMS feature info');
        let url = UrlHandler.getBaseUrl(layer) + '/wms';

        const layersParam = layer.provider.geoserverdata ?
            `${layer.provider.geoserverdata.workspace}:${layer.provider.geoserverdata.datastore}` :
            layer.name;

        // Read the active CQL_FILTER from the OL layer source
        const activeFilter = mapService.getLayerActiveFilter(layer.id);

        const wmsParams = {
            'LAYERS': layersParam,
            'TILED': true,
            'VERSION': '1.1.1',
        };
        if (activeFilter && activeFilter.cqlFilter) {
            wmsParams['CQL_FILTER'] = activeFilter.cqlFilter;
        }

        const wmsSource = new TileWMS({ url, params: wmsParams });

        const featureInfoUrl = wmsSource.getFeatureInfoUrl(
            coordinate,
            resolution,
            projection,
            {
                'INFO_FORMAT': 'application/json',
                'BUFFER': WMS_HIT_BUFFER_PX
            }
        );
        console.log('Feature info URL:', featureInfoUrl);

        if (!featureInfoUrl) return null;

        const response = await fetch(featureInfoUrl);
        if (!response.ok) return null;

        const data = await response.json();
        return {
            layerName: layer.name,
            features: data.features || []
        };
    }

    /**
     * Fetches query data from ArcGIS REST Service
     */
    async fetchArcGISFeatureInfo(layer, coordinate, resolution) {
        const baseUrl = UrlHandler.getArcgisUrl(layer)
        const buffer = ARCGIS_HIT_TOLERANCE_PX * (resolution || 1);
        const envelope = {
            xmin: coordinate[0] - buffer,
            ymin: coordinate[1] - buffer,
            xmax: coordinate[0] + buffer,
            ymax: coordinate[1] + buffer,
            spatialReference: { wkid: 3857 }
        };

        // Read active LAYERDEFS to extract the WHERE clause for this sublayer
        const activeFilter = mapService.getLayerActiveFilter(layer.id);
        let whereClause = '1=1';
        if (activeFilter && activeFilter.layerDefs) {
            try {
                const defs = JSON.parse(activeFilter.layerDefs);
                whereClause = defs[String(layer.codigo)] || '1=1';
            } catch (e) {
                // malformed LAYERDEFS — fall back to no filter
            }
        }

        const params = new URLSearchParams({
            f: 'json',
            returnGeometry: 'true',
            spatialRel: 'esriSpatialRelIntersects',
            geometry: JSON.stringify(envelope),
            geometryType: 'esriGeometryEnvelope',
            outFields: '*',
            outSR: '3857',
            where: whereClause,
        });
        let queryUrl = `${baseUrl}/query?${params.toString()}`;
        const response = await fetch(queryUrl);
        if (!response.ok) return null;
        const data = await response.json();
        const features = (data.features || []).map(f => ({
            type: 'Feature',
            geometry: null,
            properties: f.attributes,
            id: f.attributes.OBJECTID
        }));
        return {
            layerName: layer.name,
            features: features
        };
    }


    async getAreaFeatures(geometry, layers = null) {
        const targetLayers = layers || this.activeLayers;
        if (!targetLayers || targetLayers.length === 0) {
            return [];
        }

        const promises = [...targetLayers].reverse().map(async layer => {
            const provider = layer.provider ? layer.provider.name : 'wms';

            try {
                if (provider === 'geoserver') {
                    return await this.fetchWFSFeatureInfo(layer, geometry);
                } else if (provider === 'arcgis') {
                    return await this.fetchArcGISQuery(layer, geometry);
                }
            } catch (error) {
                console.warn(`Error fetching area info for layer ${layer.name}:`, error);
                return null;
            }
            return null;
        });

        const results = await Promise.all(promises);

        return results
            .filter(res => res && res.features && res.features.length > 0)
            .flatMap(res => res.features.map(f => ({
                ...f,
                layerName: res.layerName
            })));
    }

    /**
     * WFS Complex Logic for Geoserver Area Selection
     */
    async fetchWFSFeatureInfo(layer, polygonGeometry) {
        const geoserverUrl = UrlHandler.getBaseUrl(layer)
        const workspace = layer.provider.geoserverdata.workspace;
        const datastore = layer.provider.geoserverdata.datastore;
        const layerName = `${workspace}:${datastore}`;

        // 1. Get Geometry Column Name (e.g. 'the_geom', 'shape')
        const geometryColumn = await this.getGeometryColumnName(workspace, datastore, geoserverUrl);
        if (!geometryColumn) return null;

        // 2. Get Layer SRID (e.g. '32719')
        const srid = await this.getSridFromGeoServer(workspace, datastore, geoserverUrl);
        if (!srid) return null;

        // 3. Transform Polygon to Layer SRID
        const coords = polygonGeometry.getCoordinates()[0];
        const transformedCoords = coords.map(coord =>
            transform(coord, "EPSG:3857", "EPSG:" + srid)
        );

        const polygonString = transformedCoords.map(c => c.join(" ")).join(",");

        // 4. Construct CQL Filter
        const cqlFilter = `INTERSECTS(${geometryColumn}, SRID=${srid};POLYGON((${polygonString})))`;

        // 5. Fetch WFS
        const params = new URLSearchParams();
        params.append("service", "WFS");
        params.append("version", "2.0.0");
        params.append("request", "GetFeature");
        params.append("typeNames", layerName);
        params.append("outputFormat", "application/json");
        params.append("CQL_FILTER", cqlFilter);

        const response = await fetch(`${geoserverUrl}/wfs`, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: params.toString()
        });

        if (!response.ok) return null;
        const data = await response.json();

        return {
            layerName: layer.name,
            features: data.features || []
        };
    }

    async fetchArcGISQuery(layer, geometry) {
        const baseUrl = UrlHandler.getArcgisUrl(layer)

        const rings = geometry.getCoordinates();
        const esriGeometry = {
            rings: rings,
            spatialReference: { wkid: 3857 }
        };

        const params = new URLSearchParams({
            f: 'json',
            returnGeometry: 'true',
            spatialRel: 'esriSpatialRelIntersects',
            geometry: JSON.stringify(esriGeometry),
            geometryType: 'esriGeometryPolygon',
            outFields: '*',
            outSR: '3857'
        });

        const queryUrl = `${baseUrl}/query`;
        const response = await fetch(queryUrl, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: params.toString()
        });

        if (!response.ok) return null;
        const data = await response.json();

        const format = new EsriJSON();
        const geoJSONFormat = new GeoJSON();
        // Parse EsriJSON to OL Features then write to GeoJSON POJOs
        const olFeatures = format.readFeatures(data);
        const geoFeatures = olFeatures.map(f => geoJSONFormat.writeFeatureObject(f));

        return {
            layerName: layer.name,
            features: geoFeatures
        };
    }


    async getGeometryColumnName(workspace, datastore, geoserverUrl) {
        const layerName = `${workspace}:${datastore}`;
        try {
            const wfsUrl = `${geoserverUrl}/wfs?service=wfs&version=2.0.0&request=DescribeFeatureType&typeNames=${layerName}`;
            const response = await fetch(wfsUrl);
            const text = await response.text();

            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(text, "text/xml");

            const elements = xmlDoc.getElementsByTagName("xsd:element");
            for (const element of elements) {
                const type = element.getAttribute("type");
                if (type && type.includes("gml:")) {
                    return element.getAttribute("name");
                }
            }
            return 'the_geom'; // fallback
        } catch (e) {
            console.warn("Failed to get geometry column", e);
            return 'the_geom';
        }
    }

    async getSridFromGeoServer(workspace, datastore, geoserverUrl) {
        try {
            const layerName = `${workspace}:${datastore}`;
            const url = `${geoserverUrl}/wfs?service=WFS&version=2.0.0&request=GetFeature&typeNames=${layerName}&count=1&outputFormat=application/json`;
            const response = await fetch(url);
            const data = await response.json();

            if (data.crs && data.crs.properties && data.crs.properties.name) {
                return data.crs.properties.name.split(":").pop();
            }
            return '3857'; // default
        } catch (e) {
            return '3857';
        }
    }


    // --- POINT SELECTION ---
    enablePointSelection() {
        this.disable();
        if (!this.map) return;

        this.map.getViewport().style.cursor = 'pointer';
        this.clickListener = async (evt) => {
            const view = this.map.getView();
            const resolution = view.getResolution();
            const projection = view.getProjection();

            const features = await this.getPointFeatures(evt.coordinate, resolution, projection);

            // Setup selection
            this.setSelection(evt.coordinate, features);
        };

        this.map.on('singleclick', this.clickListener);
    }

    // --- AREA SELECTION ---
    enableAreaSelection(drawType) {
        console.log('drawType', drawType);
        this.disable();
        if (!this.map) return;

        if (this.selectionLayer && !this.map.getLayers().getArray().includes(this.selectionLayer)) {
            this.map.addLayer(this.selectionLayer);
        }

        this.drawInteraction = new Draw({
            source: this.selectionLayer.getSource(),
            type: drawType === 'Circle' ? 'Circle' : 'Polygon',
        });

        this.drawInteraction.on('drawstart', () => {
            this.selectionLayer.getSource().clear();
        });

        this.drawInteraction.on('drawend', async (evt) => {
            let geometry = evt.feature.getGeometry();
            if (drawType === 'Circle') {
                geometry = fromCircle(geometry);
            }
            const extent = geometry.getExtent();
            const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2];

            const features = await this.getAreaFeatures(geometry);
            this.setSelection(center, features);
        });

        this.map.addInteraction(this.drawInteraction);
    }

    disable() {
        if (!this.map) return;

        this.map.getViewport().style.cursor = '';

        if (this.clickListener) {
            this.map.un('singleclick', this.clickListener);
            this.clickListener = null;
        }

        if (this.drawInteraction) {
            this.map.removeInteraction(this.drawInteraction);
            this.drawInteraction = null;
        }

        this.clearResults();

        if (this.selectionLayer) {
            this.map.removeLayer(this.selectionLayer);
        }
    }
}

export const selectService = new SelectService();
